import {
  UsdCents,
  UsdDollars,
  zCurrency,
  zUsdCents,
  zUsdDollars,
} from "@redotech/money/usd-amounts";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { AeoCurrentUsageData } from "./aeo/aeo-billing-types";
import {
  CheckoutOptimizationCurrentUsageData,
  CheckoutOptimizationPerOrderCurrentUsageData,
} from "./checkout-optimization/checkout-optimization-billing-types";
import { DimensionalPackagingCurrentUsageData } from "./dimensional-packaging/dimensional-packaging-billing-types";
import { DisputesAlertsDeflectionsCurrentUsageData } from "./disputes-alerts-deflections/disputes-alerts-deflections-billing-types";
import { DisputesCurrentUsageData } from "./disputes/disputes-billing-types";
import { ImsCurrentUsageData } from "./ims/ims-billing-types";
import { LcgCommitCurrentUsageData } from "./lcg/lcg-billing-types";
import { MarketingEmailCurrentUsageData } from "./marketing/email-billing-types";
import { MarketingSmsCurrentUsageData } from "./marketing/sms-billing-types";
import { OmsCurrentUsageData } from "./outbound-labels/oms-billing-types";
import { WmsCurrentUsageData } from "./outbound-labels/wms-billing-types";
import { RecoverCurrentUsageData } from "./recover/recover-billing-types";
import { ReturnsAiExchangeCurrentUsageData } from "./returns/ai-exchange/ai-exchange-billing-types";
import { ReturnsCurrentUsageData } from "./returns/returns-billing-types";
import {
  BillingPeriod,
  LineItem,
  RecurringInvoiceData,
  ServiceInvoiceType,
  SupportedBillingEngineInvoiceType,
} from "./service-invoice";
import { SupportCurrentUsageData } from "./support/support-billing-types";
import { OrderTrackingCurrentUsageData } from "./tracking/tracking-billing-types";
import { CtxCurrentUsageData } from "./unified-billing/ctx-pricing";

/**
 * Core types for the unified billing system
 */

export enum PaymentProviderType {
  SHOPIFY = "shopify",
  STRIPE = "stripe",
}

export interface CreateInvoiceParams<TInvoice, TSettings> {
  teamId: string;
  lineItems: LineItem[];
  recurringInvoiceData?: RecurringInvoiceData;
  lastInvoice: TInvoice | null;
  settings: TSettings;
  breakdowns: any;
  usageBillingWindowConfigId?: string;
  billingDueDate: Date;
}

/**
 * Configuration for subscription billing returned by providers
 */
export interface SubscriptionConfig {
  planName: string; // e.g., "Email plan", "SMS credits plan"
  periodFeeInCents: UsdCents;
  renewOnPeriodEnd: boolean;
  billingPeriod: BillingPeriod;
  quantity?: number; // free emails/credits per period
  /**
   * Day-of-month [1, 28] this product should anchor to. Required for
   * BIMONTHLY (defines the primary anchor; secondary is `anchor + 15`
   * clamped to month length via `getBimonthlySecondaryDay`). Other periods
   * may pass this through as the team's intended anchor day; it is
   * informational for non-bimonthly cadences.
   */
  targetBillingDay?: number;
}

/**
 * Parameters for creating subscription line items
 */
export interface CreateSubscriptionLineItemsParams<
  TSettings extends BaseBillingSettings,
> {
  subscriptionConfig: SubscriptionConfig;
  settings: TSettings; // Provider-specific settings
  startDate: Date;
  endDate: Date;
}

/**
 * Information about billing periods calculated by the engine
 */
export interface BillingPeriodInfo {
  nextInvoiceStart: Date;
  nextInvoiceEnd: Date;
  nextPeriodStart: Date;
  nextPeriodEnd: Date;
}

/**
 * Core billing data returned by providers for current billing summary
 */
export interface CurrentBillingData<TUsageData> {
  subscriptionPrice: UsdDollars;
  totalInvoicePrice: UsdDollars;
  usage: TUsageData;
}

/**
 * Structured billing summary that replaces raw invoice calculation
 */
export interface CurrentBillingSummary<TUsageData> {
  // Common fields for all products
  subscriptionPrice: UsdDollars;
  totalInvoicePrice: UsdDollars;
  billingDueDate: Date;
  period: BillingPeriod;

  // Provider-specific usage data
  usage: TUsageData;
  bundlePercentOff?: number | null;
}

export type InvoiceTypeToUsageType<
  T extends SupportedBillingEngineInvoiceType,
> = {
  [ServiceInvoiceType.MARKETING_EMAIL]: MarketingEmailCurrentUsageData;
  [ServiceInvoiceType.MARKETING_SMS]: MarketingSmsCurrentUsageData;
  [ServiceInvoiceType.ORDER_TRACKING]: OrderTrackingCurrentUsageData;
  [ServiceInvoiceType.RETURN_AI_EXCHANGE]: ReturnsAiExchangeCurrentUsageData;
  [ServiceInvoiceType.RETURNS]: ReturnsCurrentUsageData;
  [ServiceInvoiceType.CHECKOUT_OPTIMIZATION]: CheckoutOptimizationPerOrderCurrentUsageData;
  [ServiceInvoiceType.CHECKOUT_OPTIMIZATION_SUBSCRIPTION]: CheckoutOptimizationCurrentUsageData;
  [ServiceInvoiceType.AEO]: AeoCurrentUsageData;
  [ServiceInvoiceType.IMS]: ImsCurrentUsageData;
  [ServiceInvoiceType.RECOVER]: RecoverCurrentUsageData;
  [ServiceInvoiceType.DISPUTES]: DisputesCurrentUsageData;
  [ServiceInvoiceType.DISPUTES_ALERTS_DEFLECTIONS]: DisputesAlertsDeflectionsCurrentUsageData;
  [ServiceInvoiceType.LCG_COMMIT]: LcgCommitCurrentUsageData;
  [ServiceInvoiceType.CTX_USAGE]: CtxCurrentUsageData;
  [ServiceInvoiceType.SUPPORT_BASE]: SupportCurrentUsageData;
  [ServiceInvoiceType.DIMENSIONAL_PACKAGING]: DimensionalPackagingCurrentUsageData;
  [ServiceInvoiceType.WMS]: WmsCurrentUsageData;
  [ServiceInvoiceType.OMS]: OmsCurrentUsageData;
}[T];

/** How a closed invoice gets paid. */
export enum CollectionMethod {
  /** Charge the team's payment method. */
  AUTO = "auto",
  /** The merchant pays from the Bills page; we email instead of charging. */
  MANUAL = "manual",
}

/**
 * Base billing settings interface with common fields across all billing products.
 * This replaces the need for providers to work directly with Mongoose Hydrated objects.
 */
export type BaseBillingSettings = z.infer<typeof baseBillingSettingsSchema>;
export const baseBillingSettingsSchema = z.object({
  enabled: z.boolean(),
  startDate: z.date().nullish(),
  period: createEnum(BillingPeriod, { enumName: "BillingPeriod" }),
  periodFeeInCents: zUsdCents,
  renewOnPeriodEnd: z.boolean(),
  targetBillingDay: z.number().min(1).max(28).optional(),
  /**
   * Defaults to `auto` in Mongo, but existing documents and mirror reads can
   * still lack it, and providers with their own schemas do not store it.
   * Absent reads as `auto`.
   */
  collectionMethod: createEnum(CollectionMethod, {
    enumName: "CollectionMethod",
  }).optional(),
  /**
   * Net terms. Defaults to 0 in Mongo, which collects at create — what every
   * team does today. A positive value defers collection: the invoice is closed
   * now, its due date is stamped `closedAt + netTermsDays`, and
   * `collectionMethod` decides what happens when that date arrives. Optional
   * for the same reason as `collectionMethod`; absent reads as 0.
   */
  netTermsDays: z.number().int().min(0).optional(),
});

export type BillingEngineStripeMetadata = z.infer<
  typeof billingEngineStripeMetadataSchema
>;
// Stripe metadata rejects `undefined`, so an absent value is written as `null`;
// it then reads back as an absent key, which the schema takes as optional.
export type BillingEngineStripeMetadataWrite = Omit<
  BillingEngineStripeMetadata,
  "chargeAmountInDollars"
> & { chargeAmountInDollars: UsdDollars | null };
export const billingEngineStripeMetadataSchema = z.object({
  type: z.literal("billing_engine"),
  invoiceId: z.string(),
  teamId: z.string(),
  invoiceType: createEnum(ServiceInvoiceType, {
    enumName: "ServiceInvoiceType",
  }),
  retryCount: z.number().optional(),
  /** What Stripe settles, in `chargeCurrency`'s major units. */
  chargeAmount: z
    .union([z.string().transform((val) => parseFloat(val)), z.number()])
    .optional(),
  chargeCurrency: zCurrency.optional(),
  /** Absent on localized (non-USD) charges — read `chargeAmount` instead. */
  chargeAmountInDollars: z
    .union([
      // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
      z.string().transform((val) => parseFloat(val) as UsdDollars),
      zUsdDollars,
    ])
    .optional(), // TODO: Remove optional in November 2025
});
